import { BulbOutlined, CloseOutlined } from '@ant-design/icons';

import styled from '@emotion/styled';

import { PageGuide } from '../../models/guides/pageGuides';

interface PageGuidePanelProps {
  guide: PageGuide;
  onClose: () => void;
  title?: string;
}

// "How this page works": a few practical tips in a quiet card. Shown the
// first time someone opens a page, then only when they ask for it.
const PageGuidePanel: React.FC<PageGuidePanelProps> = ({ guide, onClose, title = 'How this page works' }) => (
  <Panel role='region' aria-label={title}>
    <Icon aria-hidden>
      <BulbOutlined />
    </Icon>
    <Body>
      <strong>{title}</strong>
      <ul>
        {guide.tips.map((tip) => (
          <li key={tip}>{tip}</li>
        ))}
      </ul>
    </Body>
    <Close type='button' onClick={onClose} aria-label='Hide tips' title='Hide tips'>
      <CloseOutlined />
    </Close>
  </Panel>
);

export default PageGuidePanel;

const SEEN_PREFIX = 'hethera:guide-seen:';

export const hasSeenGuide = (key: string) => {
  try {
    return localStorage.getItem(SEEN_PREFIX + key) === '1';
  } catch {
    return true;
  }
};

export const markGuideSeen = (key: string, seen = true) => {
  try {
    if (seen) localStorage.setItem(SEEN_PREFIX + key, '1');
    else localStorage.removeItem(SEEN_PREFIX + key);
  } catch {}
};

export const GUIDES_RESET_EVENT = 'hethera:guides-reset';

// Brings every page's tips back (command menu → "Show page tips again"); the
// open page listens for the event and shows its tips at once.
export const resetAllGuides = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(SEEN_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
  window.dispatchEvent(new Event(GUIDES_RESET_EVENT));
};

const Panel = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 14px 14px 16px;
  border-radius: 12px;
  border: 1px solid #e3ecd0;
  background: linear-gradient(180deg, #f7fbec 0%, #fbfaf7 100%);
  animation: guideIn 0.2s ease-out;

  @keyframes guideIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
  }
`;

const Icon = styled.span`
  display: grid;
  place-items: center;
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--accent);
  color: var(--ink);
  font-size: 14px;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  color: var(--ink-2);

  & strong {
    display: block;
    margin: 4px 0 6px;
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
  }
  & ul {
    margin: 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    line-height: 1.5;
  }
  & li::marker {
    color: #1f7a45;
  }
`;

const Close = styled.button`
  display: grid;
  place-items: center;
  flex: none;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: var(--hover);
    color: var(--ink);
  }
`;
